import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('bourbon_theme');
    if (stored) return stored;
    return 'system';
  });
  
  const [autoTheme, setAutoTheme] = useState(() => {
    return localStorage.getItem('bourbon_auto_theme') === 'true';
  });

  // Get theme based on time of day (dark from 20h to 7h)
  const getAutoTheme = useCallback(() => {
    const hour = new Date().getHours();
    return (hour >= 20 || hour < 7) ? 'dark' : 'light';
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    
    const applyTheme = (newTheme) => {
      root.classList.remove('light', 'dark');
      
      if (newTheme === 'system') {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches 
          ? 'dark' 
          : 'light';
        root.classList.add(systemTheme);
      } else {
        root.classList.add(newTheme);
      }
    };

    // If auto theme is enabled, use time-based theme
    if (autoTheme) {
      applyTheme(getAutoTheme());
    } else {
      applyTheme(theme);
    }
    
    localStorage.setItem('bourbon_theme', theme);
    localStorage.setItem('bourbon_auto_theme', autoTheme.toString());

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (!autoTheme && theme === 'system') {
        applyTheme('system');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    
    // Check for auto theme changes every minute
    let autoThemeInterval = null;
    if (autoTheme) {
      autoThemeInterval = setInterval(() => {
        applyTheme(getAutoTheme());
      }, 60000); // Check every minute
    }
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      if (autoThemeInterval) {
        clearInterval(autoThemeInterval);
      }
    };
  }, [theme, autoTheme, getAutoTheme]);

  // Handler for setting auto theme
  const handleSetAutoTheme = useCallback((enabled) => {
    setAutoTheme(enabled);
    if (enabled) {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(getAutoTheme());
    }
  }, [getAutoTheme]);

  const contextValue = useMemo(() => ({ 
    theme, 
    setTheme, 
    autoTheme, 
    setAutoTheme: handleSetAutoTheme 
  }), [theme, autoTheme, handleSetAutoTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
